// src/components/Gallery/Scene/components/VRMAvatar.jsx
import { useRef, useEffect, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import * as THREE from 'three';

const GLBAvatar = ({ 
  vrmUrl = process.env.PUBLIC_URL + '/models/yea-host.glb', 
  animationUrl = process.env.PUBLIC_URL + '/models/yea-animation.glb',
  position = [0, 0, 0],
  movementState = { isMoving: false, isSprinting: false, speed: 0, velocity: 0 },
  onAvatarLoaded
}) => {
  const modelRef = useRef();
  const mixerRef = useRef();
  const currentAnimationRef = useRef(null);
  const [isLowPowerMode, setIsLowPowerMode] = useState(false);
  
  // Detect low power mode
  useEffect(() => {
    const checkPerformance = () => {
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const isLowEnd = navigator.hardwareConcurrency <= 4;
      setIsLowPowerMode(isMobile || isLowEnd);
    };
    checkPerformance();
  }, []);
  
  // Store animations by type
  const animationsRef = useRef({
    idle: null,
    walk: null,
    run: null
  });
  
  // Load the GLB model
  useEffect(() => {
    const loader = new GLTFLoader();
    
    console.log('Loading model from:', vrmUrl);
    
    loader.load(
      vrmUrl,
      (gltf) => {
        console.log('Model loaded successfully');
        
        // Store the model
        const model = gltf.scene;
        modelRef.current = model;
        
        // Performance optimizations for the model
        model.traverse((child) => {
          if (child.isMesh) {
            // Enable frustum culling
            child.frustumCulled = true;
            
            // Optimize materials for performance
            if (child.material) {
              if (isLowPowerMode) {
                // Low power optimizations
                child.material.precision = 'mediump';
                child.castShadow = false;
                child.receiveShadow = false;
                
                // Simplify materials
                if (child.material.map) {
                  child.material.map.generateMipmaps = false;
                  child.material.map.minFilter = THREE.LinearFilter;
                }
              } else {
                // High quality settings
                child.castShadow = true;
                child.receiveShadow = true;
              }
            }
          }
        });
        
        // Create animation mixer
        const mixer = new THREE.AnimationMixer(model);
        mixerRef.current = mixer;
        
        // Load animations from separate file
        if (animationUrl) {
          const animLoader = new GLTFLoader();
          animLoader.load(
            animationUrl,
            (animGltf) => {
              console.log('Animation file loaded successfully');
              console.log('Available animations:', animGltf.animations);
              
              if (animGltf.animations && animGltf.animations.length > 0) {
                // Log all available animations to help debug
                animGltf.animations.forEach(anim => {
                  console.log(`Animation found: ${anim.name}`);
                });
                
                // Based on user feedback, we need to remap the animations
                // The jump animation is currently playing as idle
                // Walk and run are switched
                if (animGltf.animations.length >= 4) {
                  // Store animations by type - using the 4th animation (index 3) for idle
                  // and swapping walk/run
                  animationsRef.current = {
                    idle: animGltf.animations[3],  // Fourth animation as idle
                    walk: animGltf.animations[2],  // Third animation as walk
                    run: animGltf.animations[1]    // Second animation as run
                  };
                  
                  console.log('Mapped animations:', {
                    idle: animationsRef.current.idle?.name,
                    walk: animationsRef.current.walk?.name,
                    run: animationsRef.current.run?.name
                  });
                  
                  // Play idle animation by default
                  const action = mixer.clipAction(animationsRef.current.idle);
                  action.play();
                  currentAnimationRef.current = {
                    clip: animationsRef.current.idle,
                    action: action,
                    name: 'idle'
                  };
                } else {
                  // If we don't have enough animations, just use the first one
                  const idleAnim = animGltf.animations[0];
                  console.log(`Using first animation as idle: ${idleAnim.name}`);
                  
                  const action = mixer.clipAction(idleAnim);
                  action.play();
                  currentAnimationRef.current = {
                    clip: idleAnim,
                    action: action,
                    name: 'idle'
                  };
                }
                
                // Call the loading callback when everything is ready
                onAvatarLoaded?.();
              }
            },
            undefined,
            (error) => console.error('Animation loading error:', error)
          );
        } else {
          // If no animation URL, call loaded immediately after model loads
          onAvatarLoaded?.();
        }
      },
      undefined,
      (error) => console.error('Model loading error:', error)
    );
    
    // Cleanup function
    return () => {
      if (mixerRef.current) {
        mixerRef.current.stopAllAction();
      }
    };
  }, [vrmUrl, animationUrl]);
  
  // Handle movement state changes with velocity-based animation
  useEffect(() => {
    if (!mixerRef.current || !animationsRef.current) return;
    
    let targetAnimation = animationsRef.current.idle;
    let animationName = 'idle';
    let playbackRate = 1.0;
    
    if (movementState.isMoving && movementState.speed > 0.1) {
      // Determine animation based on speed rather than just sprint flag
      const speedRatio = movementState.speed / 7.5; // Normalize against max sprint speed
      
      if (speedRatio > 0.6 && animationsRef.current.run) {
        targetAnimation = animationsRef.current.run;
        animationName = 'run';
        // Scale animation speed based on actual movement speed
        playbackRate = Math.max(0.8, Math.min(1.5, speedRatio));
      } else if (speedRatio > 0.2 && animationsRef.current.walk) {
        targetAnimation = animationsRef.current.walk;
        animationName = 'walk';
        // Scale animation speed based on actual movement speed
        playbackRate = Math.max(0.5, Math.min(1.2, speedRatio * 1.5));
      }
    }
    
    // Only change animation if it's different from current
    if (currentAnimationRef.current?.name !== animationName && targetAnimation) {
      console.log(`Changing animation to: ${animationName} (speed: ${playbackRate.toFixed(2)})`);
      
      // Fade out current animation
      if (currentAnimationRef.current?.action) {
        currentAnimationRef.current.action.fadeOut(0.3);
      }
      
      // Start new animation
      const newAction = mixerRef.current.clipAction(targetAnimation);
      newAction.reset().fadeIn(0.3).play();
      newAction.setEffectiveTimeScale(playbackRate);
      
      // Update current animation reference
      currentAnimationRef.current = {
        clip: targetAnimation,
        action: newAction,
        name: animationName
      };
    } else if (currentAnimationRef.current?.action) {
      // Update playback rate for existing animation
      currentAnimationRef.current.action.setEffectiveTimeScale(playbackRate);
    }
  }, [movementState.isMoving, movementState.isSprinting, movementState.speed]);
  
  // Update animation mixer with performance optimization
  useFrame((_, delta) => {
    if (mixerRef.current) {
      // Reduce animation update frequency on low-end devices
      const animationDelta = isLowPowerMode ? Math.min(delta, 1/30) : delta;
      mixerRef.current.update(animationDelta);
    }
  });
  
  // Simple group with the model
  return (
    <group position={position}>
      {/* Debug Collision Capsule - Hidden */}
      <group position={[0, 0.9, 0]} visible={false}>
        {/* Capsule cylinder body */}
        <mesh position={[0, 0, 0]}>
          <cylinderGeometry args={[0.3, 0.3, 1.4, 12]} />
          <meshBasicMaterial color="lime" wireframe={true} transparent={true} opacity={0.5} />
        </mesh>
        
        {/* Top hemisphere */}
        <mesh position={[0, 0.7, 0]}>
          <sphereGeometry args={[0.3, 12, 6, 0, Math.PI * 2, 0, Math.PI / 2]} />
          <meshBasicMaterial color="lime" wireframe={true} transparent={true} opacity={0.5} />
        </mesh>
        
        {/* Bottom hemisphere */}
        <mesh position={[0, -0.7, 0]}>
          <sphereGeometry args={[0.3, 12, 6, 0, Math.PI * 2, Math.PI / 2, Math.PI / 2]} />
          <meshBasicMaterial color="lime" wireframe={true} transparent={true} opacity={0.5} />
        </mesh>
      </group>
      
      {/* The GLB model */}
      {modelRef.current && (
        <primitive 
          object={modelRef.current} 
          position={[0, 0, 0]} 
          scale={[2.5, 2.5, 2.5]} 
          rotation={[0, Math.PI, 0]} 
        />
      )}
    </group>
  );
};

export default GLBAvatar;